import { put, call, select, all, takeLatest } from 'redux-saga/effects';
import {
    GET_OVERVIEW_REQUEST,
    getOverviewSuccess,
    getOverviewFailure,
    GET_OPERATIONS_REQUEST,
    getOperationsSuccess,
    getOperationsFailure,
    GET_INVENTORY_REQUEST,
    getInventorySuccess,
    getInventoryFailure,
    GET_TYPE_IDS_REQUEST,
    getTypeIdsSuccess,
    getTypeIdsFailure,
    GET_INVENTORY_ITEMS_REQUEST,
    getInventoryItemsSuccess,
    getInventoryItemsFailure,
    GET_TASK_STATUSES_REQUEST,
    getTaskStatusesSuccess,
    getTaskStatusesFailure
} from '../actions/dashboard';
import {
    getSummary,
    getKanban,
    getSeries,
    getCompaniesRanking,
    getReasons,
    getOperations,
    getInventorySummary,
    getInventoryList
} from '../services/dashboard';
import { getCatalogs } from '../services/catalogs';
import { getStatuses } from '../../tickets/services/statuses';
import { showGlobalError } from '../../app/actions/app';
import { accessTokenSelector } from '../../auth/selectors/token';
import { authSelector } from '../../home/selectors/loggedUser';

// NOTE: the flow slugs used across the dashboard UI are plural, but /dashboard/catalogs
// returns taskType names in singular (e.g. "installation", "uninstallation").
const TASK_TYPE_NAME_BY_FLOW = {
    installations: 'installation',
    maintenance: 'maintenance',
    uninstallations: 'uninstallation'
};

const TASK_TYPES = Object.keys(TASK_TYPE_NAME_BY_FLOW);

// NOTE: usuarios con `dashboard.companies.onlySame` (supervisor) solo pueden ver su compañía:
// se fuerza en cada request sin importar lo que venga de los filtros (ej. filtros guardados).
const scopeFilters = function* (filters) {
    const loggedUser = yield select(authSelector);
    const onlySame = loggedUser?.permission?.dashboard?.companies?.onlySame;
    const companyId = loggedUser?.company?._id;

    return onlySame && companyId ? { ...filters, companyId } : filters;
};

// NOTE: en inventario el back ya acota al supervisor a sus compañías (las de sus dealers activos, o la
// propia si no tiene). Mandar `company` lo limitaría a una sola, o devolvería cero si no es de las suyas.
const isOnlySameCompany = function* () {
    const loggedUser = yield select(authSelector);
    return !!loggedUser?.permission?.dashboard?.companies?.onlySame;
};

const scopeInventoryFilters = function* (filters) {
    if (!(yield* isOnlySameCompany())) return filters;
    const { company, ...rest } = filters;
    return rest;
};

const getOverviewWorker = function* ({ payload: { filters: rawFilters = {} } = {} } = {}) {
    try {
        const token = yield select(accessTokenSelector);
        const filters = yield* scopeFilters(rawFilters);

        const [
            { data: summary },
            { data: kanban },
            { data: series },
            { data: companiesResponse },
            { data: reasons }
        ] = yield all([
            call(getSummary, filters, token),
            call(getKanban, filters, token),
            call(getSeries, filters, token),
            call(getCompaniesRanking, filters, token),
            call(getReasons, filters, token)
        ]);

        yield put(getOverviewSuccess({
            summary,
            kanban,
            series,
            companies: companiesResponse.companies || [],
            reasons
        }));
    } catch (err) {
        yield put(getOverviewFailure(err));
        yield put(showGlobalError(true, err));
    }
};

const getOperationsWorker = function* ({ payload: { filters: rawFilters = {} } = {} } = {}) {
    try {
        const token = yield select(accessTokenSelector);
        const filters = yield* scopeFilters(rawFilters);

        const { data: { items = [], total = 0 } = {} } = yield call(getOperations, filters, token);

        yield put(getOperationsSuccess(items, total));
    } catch (err) {
        yield put(getOperationsFailure(err));
        yield put(showGlobalError(true, err));
    }
};

const getInventoryWorker = function* ({ payload: { filters: rawFilters = {} } = {} } = {}) {
    try {
        const token = yield select(accessTokenSelector);
        const filters = yield* scopeInventoryFilters(rawFilters);
        const onlySame = yield* isOnlySameCompany();

        // NOTE: las simcards son stock de Mavi sin compañía: para el supervisor el back devuelve vacío.
        const [
            { data: { data: gps } = {} },
            { data: { data: simcards } = {} }
        ] = yield all([
            call(getInventorySummary, filters, token),
            onlySame ? {} : call(getInventoryList, 'simcards', filters, token)
        ]);

        yield put(getInventorySuccess({ gps, simcards }));
    } catch (err) {
        yield put(getInventoryFailure(err));
        yield put(showGlobalError(true, err));
    }
};

const getTypeIdsWorker = function* () {
    try {
        const token = yield select(accessTokenSelector);

        const { data: { taskTypes = [] } = {} } = yield call(getCatalogs, token);

        const typeIds = TASK_TYPES.reduce((acc, type) => {
            const name = TASK_TYPE_NAME_BY_FLOW[type];
            const match = taskTypes.find(taskType => taskType.name === name || taskType.key === name);
            if (match) {
                acc[type] = match.id || match._id;
            }
            return acc;
        }, {});

        yield put(getTypeIdsSuccess(typeIds));
    } catch (err) {
        yield put(getTypeIdsFailure(err));
    }
};

const getInventoryItemsWorker = function* ({ payload: { type = 'gps', filters: rawFilters = {} } = {} } = {}) {
    try {
        const token = yield select(accessTokenSelector);
        const filters = yield* scopeInventoryFilters(rawFilters);

        // NOTE: despite the doc calling this "inventory lists", it actually returns an aggregate
        // breakdown object (total + nested by* dictionaries), shaped differently per type.
        const { data: { data } = {} } = yield call(getInventoryList, type, filters, token);

        yield put(getInventoryItemsSuccess(data));
    } catch (err) {
        yield put(getInventoryItemsFailure(err));
        yield put(showGlobalError(true, err));
    }
};

const getTaskStatusesWorker = function* ({ payload: { type = 'installations' } = {} } = {}) {
    try {
        const token = yield select(accessTokenSelector);

        const { data: { statuses = [] } = {} } = yield call(getStatuses, {}, type, token);

        yield put(getTaskStatusesSuccess(statuses, type));
    } catch (err) {
        yield put(getTaskStatusesFailure(err));
    }
};

export default [
    takeLatest(GET_OVERVIEW_REQUEST, getOverviewWorker),
    takeLatest(GET_OPERATIONS_REQUEST, getOperationsWorker),
    takeLatest(GET_INVENTORY_REQUEST, getInventoryWorker),
    takeLatest(GET_TYPE_IDS_REQUEST, getTypeIdsWorker),
    takeLatest(GET_INVENTORY_ITEMS_REQUEST, getInventoryItemsWorker),
    takeLatest(GET_TASK_STATUSES_REQUEST, getTaskStatusesWorker)
];
